/**
 * F-305 — Super Admin Entitlement Console (T-252-001/T-252-002).
 *
 * Extends the UK-style Super Admin panel with the F-303 entitlement engine
 * controls (the identified gap from the human value-protection directive):
 * tenant entitlement grid + detail, premium feature grant/revoke (cross-tier
 * grant surfaces the backend 409), tier set, landlord-class set (flips
 * per-PROPERTY vs per-USER seats charging basis), subscription-status
 * override, and the refund-void ledger (immutable entries, flag clear =
 * adjudication). All calls hit /api/admin-entitlements (requireSuperAdmin).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  entitlementConsoleService as svc,
  tierLabel,
  statusLabel,
  chargingBasisLabel,
  type TenantGridRow,
  type TenantEntitlementDetail,
  type EntitlementCatalog,
  type RefundVoidEntry,
} from '../../services/entitlementConsole';

const STATUS_OPTIONS = ['active', 'trial', 'past_due', 'cancelled', 'deactivated'];

const badge = (text: string, tone: 'green' | 'red' | 'amber' | 'gray' | 'blue') => {
  const tones: Record<string, string> = {
    green: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    red: 'bg-red-100 text-red-800 border-red-200',
    amber: 'bg-amber-100 text-amber-800 border-amber-200',
    blue: 'bg-blue-100 text-blue-800 border-blue-200',
    gray: 'bg-gray-100 text-gray-700 border-gray-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${tones[tone]}`}>
      {text}
    </span>
  );
};

const statusTone = (status?: string | null): 'green' | 'red' | 'amber' | 'gray' | 'blue' => {
  switch (String(status || '').toLowerCase()) {
    case 'active': return 'green';
    case 'trial': return 'blue';
    case 'past_due': return 'amber';
    case 'cancelled':
    case 'canceled':
    case 'deactivated': return 'red';
    default: return 'gray';
  }
};

interface Props {
  embedded?: boolean;
}

const EntitlementConsole: React.FC<Props> = ({ embedded = true }) => {
  const [catalog, setCatalog] = useState<EntitlementCatalog | null>(null);
  const [rows, setRows] = useState<TenantGridRow[]>([]);
  const [gridError, setGridError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [lookupId, setLookupId] = useState('');
  const [selected, setSelected] = useState<TenantEntitlementDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null); // 409 surface
  const [notice, setNotice] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  // refund-void ledger (T-252-002)
  const [ledger, setLedger] = useState<{ entries: RefundVoidEntry[]; refundVoid: boolean } | null>(null);
  const [rvEntitlement, setRvEntitlement] = useState('');
  const [rvKind, setRvKind] = useState('refund');
  const [rvNote, setRvNote] = useState('');

  const loadGrid = useCallback(async () => {
    setLoading(true);
    setGridError(null);
    try {
      const [cat, list] = await Promise.all([svc.getCatalog(), svc.listTenants()]);
      setCatalog(cat);
      setRows(list.rows);
    } catch (err: any) {
      setGridError(err?.response?.data?.message || err?.message || 'Failed to load entitlement console data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadGrid(); }, [loadGrid]);

  const openTenant = useCallback(async (tenantId: string) => {
    if (!tenantId) return;
    setDetailLoading(true);
    setDetailError(null);
    setConflict(null);
    setNotice(null);
    setSelected(null);
    setLedger(null);
    try {
      const detail = await svc.getTenantDetail(tenantId);
      setSelected(detail);
      try {
        const lv = await svc.listRefundVoid(tenantId);
        setLedger({ entries: lv.entries || [], refundVoid: !!lv.refundVoid });
      } catch {
        setLedger({ entries: [], refundVoid: false });
      }
    } catch (err: any) {
      setDetailError(err?.response?.data?.message || err?.message || `Failed to load tenant ${tenantId}`);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const runAction = useCallback(async (key: string, fn: () => Promise<any>, successMsg: string) => {
    setBusyKey(key);
    setConflict(null);
    setNotice(null);
    try {
      await fn();
      setNotice(successMsg);
      await openTenant(selected?.tenantId || '');
      await loadGrid();
    } catch (err: any) {
      if (err?.status === 409) {
        setConflict(err.message);
      } else {
        setDetailError(err?.response?.data?.message || err?.message || 'Action failed');
      }
    } finally {
      setBusyKey(null);
    }
  }, [openTenant, loadGrid, selected]);

  const entitlementOptions = useMemo(() => {
    if (!catalog) return [] as Array<{ id: string; label: string }>;
    return Object.values(catalog.entitlements).map((e) => ({ id: e.id, label: e.name }));
  }, [catalog]);

  return (
    <div className={embedded ? '' : 'p-6'}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Entitlement Console</h3>
          <p className="text-sm text-gray-500">
            Value-protection controls for the F-303 entitlement engine — premium feature grants, tiers, charging basis and refund-void adjudication.
          </p>
        </div>
        <button
          onClick={loadGrid}
          className="px-3 py-2 text-xs font-bold rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
        >
          <i className="fas fa-sync mr-2"></i>Refresh
        </button>
      </div>

      {/* Tenant lookup */}
      <div className="flex gap-2 mb-4">
        <input
          type="text"
          placeholder="Open a tenant by ID…"
          value={lookupId}
          onChange={(e) => setLookupId(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') openTenant(lookupId.trim()); }}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-800"
        />
        <button
          onClick={() => openTenant(lookupId.trim())}
          disabled={!lookupId.trim() || detailLoading}
          className="px-4 py-2 bg-zinc-900 text-white text-sm font-bold rounded-lg hover:bg-zinc-800 disabled:opacity-50"
        >
          Open
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-500 text-sm">Loading entitlement catalog and tenants…</div>
      ) : gridError ? (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 text-sm mb-4">{gridError}</div>
      ) : (
        <div className="border border-gray-200 rounded-xl overflow-hidden mb-6">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Tenant</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Tier</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Charging basis</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Overrides</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Refund-void</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">No entitlement records yet — records appear when a US tenant's plan state is first stored.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.tenantId} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{r.tenantId}</td>
                  <td className="px-4 py-3 text-sm">{tierLabel(r.tier)}</td>
                  <td className="px-4 py-3 text-sm">{badge(statusLabel(r.subscriptionStatus), statusTone(r.subscriptionStatus))}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{chargingBasisLabel(r.landlordClass === 'independent' ? 'property' : r.landlordClass === 'enterprise' ? 'seats' : 'seats')}</td>
                  <td className="px-4 py-3 text-sm">{r.overriddenEntitlements > 0 ? badge(`${r.overriddenEntitlements}`, 'amber') : '—'}</td>
                  <td className="px-4 py-3 text-sm">{r.refundVoid ? badge('FLAGGED', 'red') : '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => { setLookupId(r.tenantId); openTenant(r.tenantId); }}
                      aria-label={`Manage ${r.tenantId}`}
                      className="px-3 py-1.5 text-xs font-bold rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                    >
                      Manage
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detailLoading && <div className="py-8 text-center text-gray-500 text-sm">Loading tenant entitlements…</div>}
      {detailError && <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 text-sm mb-4">{detailError}</div>}
      {conflict && (
        <div className="bg-amber-50 border border-amber-300 text-amber-900 rounded-lg p-4 text-sm mb-4">
          <strong>409 — grant rejected:</strong> {conflict}
        </div>
      )}
      {notice && <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-4 text-sm mb-4">{notice}</div>}

      {selected && catalog && (
        <div className="space-y-6">
          {/* Tenant controls */}
          <div className="border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-bold text-gray-900">
                {selected.tenantId}
                <span className="ml-2 text-sm font-normal text-gray-500">
                  {tierLabel(selected.record?.tier)} · {statusLabel(selected.record?.subscriptionStatus)}
                </span>
              </h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <TierControl detail={selected} catalog={catalog} busy={busyKey} onApply={(tier, reason) => runAction(`tier:${tier}`, () => svc.setTier(selected.tenantId, tier, reason), `Tier set to ${tierLabel(tier)}`)} />
              <StatusControl detail={selected} busy={busyKey} onApply={(status, reason) => runAction(`status:${status}`, () => svc.setSubscriptionStatus(selected.tenantId, status, reason), `Subscription status set to ${statusLabel(status)}`)} />
              <ClassControl detail={selected} catalog={catalog} busy={busyKey} onApply={(cls, reason) => runAction(`class:${cls}`, () => svc.setLandlordClass(selected.tenantId, cls, reason), `Landlord class set to ${cls} — charging basis flipped`)} />
            </div>
            {selected.effective && (
              <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-700">
                <strong>Effective allowance:</strong>{' '}
                {chargingBasisLabel(selected.effective.chargingBasis)} ·{' '}
                basis quantity {selected.effective.basisQuantity ?? '—'} ·{' '}
                per-unit {selected.effective.perBasis ?? '—'} ·{' '}
                <strong>
                  {selected.effective.unlimited
                    ? 'Unlimited'
                    : `${selected.effective.used}/${selected.effective.allowance ?? '—'} used · ${selected.effective.remaining ?? 0} left`}
                </strong>
              </div>
            )}
          </div>

          {/* Resolved entitlements */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 font-bold text-gray-900 text-sm">Premium entitlements</div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-white">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Feature</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Tier access</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Metered</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Source</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Admin actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(catalog.tierConfigs
                  .filter((t) => t.tier === (selected.record?.tier || 'freemium'))
                  .flatMap((t) => t.entitlements || [])
                  .concat(entitlementOptions.map((e) => e.id))
                  .filter((id, i, arr) => arr.indexOf(id) === i))
                  .map((id) => {
                    const resolvedEnts = selected.resolved?.entitlements || [];
                    const cat = catalog.entitlements[id];
                    const tierAllows = resolvedEnts.includes(id);
                    const override = selected.record?.premiumOverrides?.[id];
                    return (
                      <tr key={id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm font-medium text-gray-900">{cat?.name || id}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">{tierAllows ? 'Included' : 'Not in tier'}</td>
                        <td className="px-4 py-2 text-sm">{cat?.metered ? 'Per-use' : 'Flat'}</td>
                        <td className="px-4 py-2 text-sm">
                          {override !== undefined
                            ? badge(override ? 'OVERRIDE: GRANTED' : 'OVERRIDE: REVOKED', override ? 'green' : 'red')
                            : tierAllows ? badge('TIER', 'gray') : badge('—', 'gray')}
                        </td>
                        <td className="px-4 py-2 text-right space-x-1 whitespace-nowrap">
                          <button
                            disabled={busyKey !== null || override === true}
                            onClick={() => runAction(`grant:${id}`, () => svc.setEntitlementOverride(selected.tenantId, id, true), `${cat?.name || id} granted`)}
                            aria-label={`Grant ${id}`}
                            className="px-2 py-1 text-xs font-bold rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
                          >
                            Grant
                          </button>
                          <button
                            disabled={busyKey !== null || (override === false || (override === undefined && !tierAllows))}
                            onClick={() => runAction(`revoke:${id}`, () => svc.setEntitlementOverride(selected.tenantId, id, false), `${cat?.name || id} revoked`)}
                            aria-label={`Revoke ${id}`}
                            className="px-2 py-1 text-xs font-bold rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
                          >
                            Revoke
                          </button>
                          <button
                            disabled={busyKey !== null || override === undefined}
                            onClick={() => runAction(`clear:${id}`, () => svc.clearEntitlementOverride(selected.tenantId, id), `${cat?.name || id} override cleared — tier map applies`)}
                            aria-label={`Clear ${id}`}
                            className="px-2 py-1 text-xs font-bold rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                          >
                            Clear
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
            <div className="px-4 py-2 text-xs text-gray-500 border-t border-gray-100">
              Cross-tier grants are rejected by the backend (409) so admin generosity cannot bypass billing. Clear removes the override and restores the tier map.
            </div>
          </div>

          {/* Refund-void ledger (T-252-002) */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <span className="font-bold text-gray-900 text-sm">Refund-void ledger</span>
              {ledger?.refundVoid
                ? badge('REFUND-VOID FLAG SET', 'red')
                : badge('No open flag', 'green')}
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-white">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Recorded</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Entitlement</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Kind</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Note</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(ledger?.entries || []).length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">No refund-void events recorded for this tenant.</td></tr>
                )}
                {(ledger?.entries || []).map((e) => (
                  <tr key={e.id}>
                    <td className="px-4 py-2 text-sm text-gray-600">{new Date(e.createdAt).toLocaleString()}</td>
                    <td className="px-4 py-2 text-sm">{e.entitlementId ? (catalog.entitlements[e.entitlementId]?.name || e.entitlementId) : '—'}</td>
                    <td className="px-4 py-2 text-sm">{e.kind}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{e.note || '—'}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{e.createdBy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-4 py-3 border-t border-gray-100 grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
              <select value={rvEntitlement} onChange={(e) => setRvEntitlement(e.target.value)} aria-label="Refund-void entitlement" className="px-2 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="">Entitlement…</option>
                {entitlementOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
              <input value={rvKind} onChange={(e) => setRvKind(e.target.value)} placeholder="kind (refund/void)" aria-label="Refund-void kind" className="px-2 py-2 border border-gray-300 rounded-lg text-sm" />
              <input value={rvNote} onChange={(e) => setRvNote(e.target.value)} placeholder="Adjudication note…" aria-label="Refund-void note" className="px-2 py-2 border border-gray-300 rounded-lg text-sm" />
              <div className="flex gap-2">
                <button
                  disabled={busyKey !== null || !rvEntitlement}
                  onClick={() => runAction('rv:record', () => svc.recordRefundVoid(selected.tenantId, { entitlementId: rvEntitlement, kind: rvKind, note: rvNote }), 'Refund-void event recorded')}
                  className="flex-1 px-3 py-2 text-xs font-bold rounded-lg bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-40"
                >
                  Record event
                </button>
                {ledger?.refundVoid && (
                  <button
                    disabled={busyKey !== null}
                    onClick={() => runAction('rv:clear', () => svc.clearRefundVoidFlag(selected.tenantId), 'Flag cleared — adjudication recorded (entries remain immutable)')}
                    className="flex-1 px-3 py-2 text-xs font-bold rounded-lg border border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100 disabled:opacity-40"
                  >
                    Clear flag (adjudicate)
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── inline controls ─────────────────────────────────────────────────────────

function useControlState() {
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  return { value, setValue, reason, setReason };
}

function TierControl({ detail, catalog, busy, onApply }: {
  detail: TenantEntitlementDetail;
  catalog: EntitlementCatalog;
  busy: string | null;
  onApply: (tier: string, reason: string) => void;
}) {
  const s = useControlState();
  const current = detail.record?.tier || 'freemium';
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Tier — currently {tierLabel(current)}</label>
      <div className="flex gap-1">
        <select value={s.value} onChange={(e) => s.setValue(e.target.value)} className="flex-1 px-2 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">Choose…</option>
          {catalog.planOrder.map((t) => <option key={t} value={t}>{tierLabel(t)}</option>)}
        </select>
        <input value={s.reason} onChange={(e) => s.setReason(e.target.value)} placeholder="reason" className="w-24 px-2 py-2 border border-gray-300 rounded-lg text-sm" />
        <button disabled={busy !== null || !s.value} onClick={() => { onApply(s.value, s.reason); s.setValue(''); s.setReason(''); }} className="px-3 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 disabled:opacity-40">Set</button>
      </div>
    </div>
  );
}

function StatusControl({ detail, busy, onApply }: {
  detail: TenantEntitlementDetail;
  busy: string | null;
  onApply: (status: string, reason: string) => void;
}) {
  const s = useControlState();
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Subscription status — currently {statusLabel(detail.record?.subscriptionStatus)}</label>
      <div className="flex gap-1">
        <select value={s.value} onChange={(e) => s.setValue(e.target.value)} className="flex-1 px-2 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">Choose…</option>
          {STATUS_OPTIONS.map((st) => <option key={st} value={st}>{statusLabel(st)}</option>)}
        </select>
        <input value={s.reason} onChange={(e) => s.setReason(e.target.value)} placeholder="reason" className="w-24 px-2 py-2 border border-gray-300 rounded-lg text-sm" />
        <button disabled={busy !== null || !s.value} onClick={() => { onApply(s.value, s.reason); s.setValue(''); s.setReason(''); }} className="px-3 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 disabled:opacity-40">Set</button>
      </div>
    </div>
  );
}

function ClassControl({ detail, catalog, busy, onApply }: {
  detail: TenantEntitlementDetail;
  catalog: EntitlementCatalog;
  busy: string | null;
  onApply: (cls: string, reason: string) => void;
}) {
  const s = useControlState();
  const current = detail.record?.landlordClass || 'independent';
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
        Landlord class — currently {current} ({chargingBasisLabel(current === 'independent' ? 'property' : 'seats')})
      </label>
      <div className="flex gap-1">
        <select value={s.value} onChange={(e) => s.setValue(e.target.value)} aria-label="Landlord class" className="flex-1 px-2 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">Choose…</option>
          {(catalog.landlordClasses.length > 0
            ? catalog.landlordClasses
            : [
                { id: 'independent', label: 'Independent Landlord' },
                { id: 'management_company', label: 'Property Management Company' },
                { id: 'enterprise', label: 'Enterprise / Institutional' },
              ]
          ).map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <input value={s.reason} onChange={(e) => s.setReason(e.target.value)} placeholder="reason" aria-label="Landlord class reason" className="w-24 px-2 py-2 border border-gray-300 rounded-lg text-sm" />
        <button disabled={busy !== null || !s.value} onClick={() => { onApply(s.value, s.reason); s.setValue(''); s.setReason(''); }} aria-label="Set landlord class" className="px-3 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 disabled:opacity-40">Set</button>
      </div>
    </div>
  );
}

export default EntitlementConsole;
